import classNames from "classnames";

const STATUS_CLASS = "m-email-signup__message";
const FORM_SUCCESS_CLASS = "m-email-signup__form--success";

export function showMailChimpStatusMessage(
  statusEl,
  message,
  isError,
  isOnResponse
) {
  statusEl.innerHTML = message;
  statusEl.className = classNames("m-email-signup__message", {
    [`${STATUS_CLASS}--error`]: isError,
    [`${STATUS_CLASS}--success`]: !isError,
  });
  const formEl = document.querySelector("[data-behavior='SubmitEmailSignup']");
  !isError &&
    !formEl.classList.contains(FORM_SUCCESS_CLASS) &&
    formEl.classList.add(FORM_SUCCESS_CLASS);

  if (!isOnResponse) return;
  // if submit link prop provided, go to link (regardless of success or error)
  const formProps = formEl.getAttribute("data-props");
  if (!formProps) return;
  const { onSubmitLink } = JSON.parse(formProps);
  if (!onSubmitLink) return;
  window.location.href = onSubmitLink;
}

function removeMailChimpStatusMessage(statusEl) {
  const formEl = statusEl.parentNode;
  if (formEl.classList.contains(FORM_SUCCESS_CLASS)) return; // don't clear message on blur after success
  statusEl.innerHTML = "";
  statusEl.className = STATUS_CLASS;
}

export function displayMailChimpStatus({ result, msg }) {
  if (!result || !msg) return console.error("Response has wrong format"); // eslint-disable-line no-console
  const isError = result === "error";
  const mailChimpStatusEl = document.querySelector(
    "[data-ref='mailChimpStatusEl']"
  );
  if (!mailChimpStatusEl)
    // eslint-disable-next-line no-console
    return console.error(
      "Callback message can’t be rendered: status message element doesn’t exist in DOM.",
      `Response: ${msg}`
    );
  showMailChimpStatusMessage(mailChimpStatusEl, msg, isError, true);
}

/*
 * Submit Mailchimp Signup Form
 *
 * Validates an email input, provides validation feedback to user, and if valid,
 * submits the form to Mailchimp via AJAX, and renders Mailchimp's response to the user
 *
 * The submit callback from Mailchimp must be done using JSONP, as MC doesn't support CORS currently
 * A function is defined above and attached to the window object in the webpack manifest
 *
 * based on https://css-tricks.com/form-validation-part-2-constraint-validation-api-javascript/
 * and https://css-tricks.com/form-validation-part-4-validating-mailchimp-subscribe-form/
 */
export default class SubmitEmailSignup {
  constructor(el, props, refs) {
    this.el = el;
    this.props = props;
    this.refs = refs;

    // we'll use our own validation for the form
    el.setAttribute("novalidate", true);
    this.buildStatusEl();
    this.bindEvents();
  }

  get hasStatusClass() {
    return "has-status-message";
  }

  get allInputs() {
    return this.form.querySelectorAll("input");
  }

  get emailInput() {
    return this.form.querySelector("input[type='email']");
  }

  get inputId() {
    return this.emailInput.id || this.emailInput.name;
  }

  get submit() {
    return this.form.querySelector("[type='submit']");
  }

  get form() {
    return this.el;
  }

  get statusEl() {
    return this.form.querySelector("[data-ref='mailChimpStatusEl']");
  }

  buildStatusEl() {
    const field = this.emailInput;
    const el = document.createElement("span");
    el.className = STATUS_CLASS;
    el.id = "error-for-" + this.inputId;
    el.setAttribute("data-ref", "mailChimpStatusEl");

    field.setAttribute("aria-describedby", "error-for-" + this.inputId);

    field.parentNode.insertBefore(el, field.nextSibling);
  }

  bindEvents() {
    document.addEventListener("blur", this.handleBlur, true);
    this.submit.addEventListener("click", this.handleSubmit);
  }

  handleBlur = (event) => {
    if (event.target.form !== this.form) return;

    const error = this.hasError(event.target);

    if (error) return showMailChimpStatusMessage(this.statusEl, error, true);

    // don't remove error if focus shifts to submit button
    if (event.target === this.submit) return;

    removeMailChimpStatusMessage(this.statusEl);
  };

  handleSubmit = (event) => {
    event.preventDefault();

    const field = this.emailInput;
    const error = this.hasError(field);

    if (error) {
      showMailChimpStatusMessage(this.statusEl, error, true);
      return field.focus();
    }

    // otherwise submit form
    this.submitForm();
  };

  submitForm() {
    const url = this.buildSubmitUrl();

    // Create script with url and callback (if specified)
    const script = window.document.createElement("script");
    script.src = url;

    // Insert script tag into the DOM
    const ref = window.document.getElementsByTagName("script")[0];
    ref.parentNode.insertBefore(script, ref);

    // After the script is loaded (and executed), remove it
    script.onload = function () {
      this.remove();
    };
  }

  hasError(field) {
    if (field.type !== "email") return;

    const validity = field.validity;

    if (validity.valid) return;
    if (validity.typeMismatch) return "Please enter a valid e-mail address.";
    if (validity.valueMissing) return "This is a required field.";
    // If all else fails, return a generic error
    return "The value you entered for this field is invalid.";
  }

  serializeData() {
    let output = "";

    this.allInputs.forEach((field) => {
      if (!field.name)
        // eslint-disable-next-line no-console
        return console.error(
          `Form field #${field.id} requires a name attribute for data to be serialized.` // eslint-disable-line max-len
        );

      output +=
        "&" +
        encodeURIComponent(field.name) +
        "=" +
        encodeURIComponent(field.value);
    });
    return output;
  }

  buildSubmitUrl() {
    let url = this.form.getAttribute("action");
    const serializedData = this.serializeData();
    const callback = "displayMailChimpStatus";
    url += this.serializeData() + `&c=${callback}`;
    return url;
  }
}
